/**
 * useCanonicalNpcsLibrary — 2026-06-08 F3-FIX wire-up for the canonical
 * Core Rulebook Chapter 18 named NPCs.
 *
 * Mirrors useQBranchPersonnelLibrary.ts pattern: 3-state union + cancelled
 * flag unmount safety + memoised service stabilisation + 3-tab state
 * (allies/enemies/see_chapter_1).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CanonicalNpcManifestEntry } from '../services/host/CanonicalNpcsServiceModule';
import { CanonicalNpcsServiceModule } from '../services/host/CanonicalNpcsServiceModule';

export type CanonicalNpcsLibraryState =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error';

export type CanonicalNpcsLibraryTab =
  | 'allies'
  | 'enemies'
  | 'see_chapter_1';

export interface CanonicalNpcsLibraryHookResult {
  state: CanonicalNpcsLibraryState;
  npcs: CanonicalNpcManifestEntry[];
  pendingRemainingCount: number;
  error: string | null;
  refresh: () => void;
  activeTab: CanonicalNpcsLibraryTab;
  setActiveTab: (tab: CanonicalNpcsLibraryTab) => void;
  selectedNpcId: string | null;
  selectedNpcData: Record<string, unknown> | null;
  selectedNpcState: CanonicalNpcsLibraryState;
  selectNpc: (id: string | null) => void;
}

export function useCanonicalNpcsLibrary(
  npcsService?: CanonicalNpcsServiceModule,
): CanonicalNpcsLibraryHookResult {
  const resolvedService = useMemo(
    () => npcsService ?? new CanonicalNpcsServiceModule(),
    [npcsService],
  );

  const [state, setState] = useState<CanonicalNpcsLibraryState>('idle');
  const [npcs, setNpcs] = useState<CanonicalNpcManifestEntry[]>([]);
  const [pendingRemainingCount, setPendingRemainingCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const [activeTab, setActiveTab] =
    useState<CanonicalNpcsLibraryTab>('allies');

  const [selectedNpcId, setSelectedNpcId] = useState<string | null>(null);
  const [selectedNpcData, setSelectedNpcData] = useState<
    Record<string, unknown> | null
  >(null);
  const [selectedNpcState, setSelectedNpcState] =
    useState<CanonicalNpcsLibraryState>('idle');

  const refresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  const selectNpc = useCallback((id: string | null) => {
    setSelectedNpcId(id);
    if (id === null) {
      setSelectedNpcData(null);
      setSelectedNpcState('idle');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    setState('loading');
    setError(null);

    (async () => {
      try {
        const result = await resolvedService.list();
        if (cancelled) return;
        setNpcs(result.npcs);
        setPendingRemainingCount(result.pending_remaining_count);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setNpcs([]);
        setError(e instanceof Error ? e.message : String(e));
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, refreshTick]);

  useEffect(() => {
    if (selectedNpcId === null) {
      return;
    }
    let cancelled = false;

    setSelectedNpcState('loading');
    setSelectedNpcData(null);

    (async () => {
      try {
        const result = await resolvedService.get(selectedNpcId);
        if (cancelled) return;
        if (result === null) {
          setSelectedNpcState('error');
          return;
        }
        setSelectedNpcData(result);
        setSelectedNpcState('ready');
      } catch {
        if (cancelled) return;
        setSelectedNpcState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedService, selectedNpcId]);

  return {
    state,
    npcs,
    pendingRemainingCount,
    error,
    refresh,
    activeTab,
    setActiveTab,
    selectedNpcId,
    selectedNpcData,
    selectedNpcState,
    selectNpc,
  };
}
